import { differenceInMonths, differenceInYears } from 'date-fns';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInHours from 'date-fns/differenceInHours';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import format from 'date-fns/format';
import _ from 'lodash';
import { StrOrNum } from '../types/util';

export const getExcerpt = (description: string | undefined | null, maxChar = 240): string | null => {
  if (!description) {
    return null;
  }
  /**
   * If there is only one sentence
   */
  if (description.length <= maxChar + 3) {
    return description;
  } else {
    return description.substring(0, maxChar) + '...';
  }
};

export const getFormattedDate = (dateString: string): string => {
  const dateObj = new Date(dateString);

  // In case of invalid date passed
  if (isNaN(dateObj.getTime())) {
    return dateString;
  } else {
    return format(dateObj, 'dd LLLL, yyyy');
  }
};

/**
 * Get the formatted time string between dates
 * Input: startDateString: '2021-01-01 00:00:00', endDateString: '2021-01-11 00:04:00'
 * Output: '10 Days 4 Hours'
 */
export const getFormattedTimeBetweenDates = (startDateString: string, endDateString?: string): string => {
  const startDateObj = new Date(startDateString);
  const endDateObj = endDateString ? new Date(endDateString) : new Date();

  // In case of invalid date passed
  if (isNaN(startDateObj.getTime()) || isNaN(startDateObj.getTime())) {
    return 'Sometime';
  } else {
    const remainingMinutes = differenceInMinutes(endDateObj, startDateObj) % 60;
    const remainingHours = differenceInHours(endDateObj, startDateObj) % 24;
    const remainingDays = differenceInDays(endDateObj, startDateObj) % 30;
    const remainingMonths = differenceInMonths(endDateObj, startDateObj) % 12;
    const remainingYears = differenceInYears(endDateObj, startDateObj) % 365;

    let formattedTime: string;
    const years = remainingYears > 0 ? `${remainingYears} Year${remainingYears > 1 ? 's' : ''}` : '';
    const months = remainingMonths > 0 ? `${remainingMonths} Month${remainingMonths > 1 ? 's' : ''}` : '';
    const days = remainingDays > 0 ? `${remainingDays} Day${remainingDays > 1 ? 's' : ''}` : '';
    const hours = remainingHours > 0 ? `${remainingHours} Hour${remainingHours > 1 ? 's' : ''}` : '';
    const minutes = remainingMinutes > 0 ? `${remainingMinutes} Minute${remainingMinutes > 1 ? 's' : ''}` : '';
    if (years.length > 0) {
      formattedTime = `${years} ${months}`;
    } else if (months.length > 0) {
      formattedTime = `${months} ${days}`;
    } else if (days.length > 0) {
      formattedTime = `${days} ${hours}`;
    } else if (hours.length > 0) {
      formattedTime = `${hours} ${minutes}`;
    } else if (minutes.length > 0) {
      formattedTime = minutes;
    } else {
      formattedTime = 'Less than a minute';
    }
    return formattedTime;
  }
};

export const isValidDateString = (dateString?: string, separator = '-'): boolean => {
  if (!dateString) {
    return false;
  }
  const [year, month, date] = dateString.split(separator);
  if (!year || !month || !date) {
    return false;
  }
  if (isNaN(new Date(dateString).getTime())) {
    return false;
  }
  return true;
};

export const isValidDate = (date: string | number | Date): boolean => {
  const dateObj = date instanceof Date ? date : new Date(date);
  return !isNaN(dateObj.getTime());
};

export const createExcerptFromArray = (
  arr: StrOrNum[] = [],
  excerptLength = 40,
  endSeparator: 'or' | 'and' = 'and'
): string => {
  let excerpt = '';
  if (arr && arr.length > 0) {
    _.forEach(arr, (item, index) => {
      if (excerpt.length + item.toString().length < excerptLength) {
        if (index !== 0) {
          if (index !== arr.length - 1) {
            excerpt += ', ';
          } else {
            excerpt += ` ${endSeparator} `;
          }
        }
        excerpt += item.toString();
      } else {
        return excerpt + '..';
      }
    });
  }
  return excerpt;
};

export const abbreviateNumber = (value: number) => {
  const suffixes = ['', 'K', 'M', 'B', 'T'];
  const suffixNum = Math.floor(value.toString().length / 3);
  let shortValue: StrOrNum = parseFloat((suffixNum !== 0 ? value / Math.pow(1000, suffixNum) : value).toPrecision(2));
  if (shortValue % 1 !== 0) {
    shortValue = shortValue.toFixed(1);
  }
  return shortValue + suffixes[suffixNum];
};

export const formatToNepaliNumber = (value: number, prefix?: string, suffix?: string) => {
  const valueInStr = value.toString();
  const [wholeValue, decimalValue] = valueInStr.split('.');
  let formattedNumber = prefix || '';

  if (wholeValue.length > 3) {
    const REGEX_TO_SEPARATE_BY_COMMA = /\B(?=(\d{2})+(?!\d))/g;
    const lastThreeValue = wholeValue.slice(-3);
    const otherValue = wholeValue.slice(0, wholeValue.length - 3);
    formattedNumber += `${otherValue.replace(REGEX_TO_SEPARATE_BY_COMMA, ',')},${lastThreeValue}`;
  } else {
    formattedNumber = wholeValue;
  }

  // Adding decimal values at last of the string
  if (decimalValue) formattedNumber += `.${decimalValue}`;

  // Adding suffix to the last of the string
  if (suffix) formattedNumber += suffix;
  return formattedNumber;
};

export const formatToNepaliPrice = (value: number) => {
  return formatToNepaliNumber(value, 'Nrs. ');
};

export const stripHTML = (text: string): string => {
  const REGEX_TO_STRIP_HTML = /(<([^>]+)>)/gi;
  return text.replace(REGEX_TO_STRIP_HTML, '');
};

export const getFormattedDateTime = (dateString: string): string => {
  const dateObj = new Date(dateString);

  // In case of invalid date passed
  if (isNaN(dateObj.getTime())) {
    return dateString;
  } else {
    return format(dateObj, 'hh:mm a, dd LLLL, yyyy');
  }
};
